import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { openRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, ArrowLeft, Truck, Mail } from "lucide-react";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, clearCart } = useCart();
  const { format, currency } = useCurrency();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [buyer, setBuyer] = useState({ name: "", email: "" });
  useEffect(() => { if (user) setBuyer((b) => ({ name: b.name || user.name || "", email: b.email || user.email || "" })); }, [user]);
  const [shipping, setShipping] = useState({
    name: "", email: "", phone: "",
    address_line1: "", address_line2: "",
    city: "", state: "", postal_code: "", country: "India",
  });

  const hasPhysical = items.some((i) => i.type === "physical" || i.type === "bundle");

  const validShipping = () => {
    if (!hasPhysical) return true;
    return shipping.name && shipping.email && shipping.address_line1 && shipping.city && shipping.postal_code && shipping.country;
  };

  const checkout = async () => {
    if (items.length === 0) return;
    if (hasPhysical && !validShipping()) {
      toast.error("Please complete the shipping fields");
      return;
    }
    if (!hasPhysical && (!buyer.email || !/\S+@\S+\.\S+/.test(buyer.email))) {
      toast.error("Please enter your email — your order is delivered there");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        currency,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        ...(hasPhysical ? { shipping } : { buyer_name: buyer.name, buyer_email: buyer.email }),
      };
      const { data } = await api.post("/checkout/cart", payload);
      openRazorpay(data, {
        onSuccess: (orderId) => { window.location.href = `/payment/success?session_id=${orderId}`; },
        onFailure: (dismissed) => {
          if (!dismissed) toast.error("Payment failed. Please try again.");
          setLoading(false);
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative" data-testid="cart-page">
      <div className="absolute -top-20 right-0 w-96 h-96 bg-peach/30 organic-blob blur-3xl -z-0 pointer-events-none" />

      <Link to="/shop" className="text-ink/60 hover:text-lapis text-xs tracking-[0.2em] uppercase mb-8 inline-flex items-center gap-2 font-semibold" data-testid="cart-back-shop">
        <ArrowLeft size={14} /> Continue browsing
      </Link>

      <div className="overline mb-5">Your basket</div>
      <h1 className="font-serif text-5xl md:text-6xl text-lapis font-light leading-tight mb-12">
        A small <em className="text-peach-deep">gathering</em>.
      </h1>

      {items.length === 0 ? (
        <div className="text-center py-24" data-testid="cart-page-empty">
          <ShoppingBag className="mx-auto mb-6 text-ink/30" size={48} strokeWidth={1.2} />
          <div className="font-serif italic text-3xl text-ink/50 mb-3">your basket is light</div>
          <div className="text-ink/60 mb-8">Open and waiting.</div>
          <Link to="/shop" className="btn-primary">Explore the shop</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-5">
            {items.map((item) => (
              <div key={item.product_id} className="flex gap-5 glass p-5 rounded-[1.5rem]" data-testid={`cart-page-item-${item.slug}`}>
                <img src={item.image} alt={item.title} className="w-24 h-28 object-cover rounded-2xl flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="overline text-[0.6rem] mb-1">{item.type}</div>
                  <div className="font-serif text-xl text-ink leading-tight">{item.title}</div>
                  <div className="text-lapis font-medium mt-1">{format(item.price, item.price_inr)}</div>
                  <div className="mt-4 flex items-center gap-4">
                    <div className="flex items-center gap-1 bg-cream/60 rounded-full">
                      <button onClick={() => updateQty(item.product_id, Math.max(0, item.quantity - 1))} className="w-8 h-8 rounded-full hover:bg-peach/40 text-lapis" data-testid={`cart-page-decrease-${item.slug}`}>
                        <Minus size={14} className="mx-auto" />
                      </button>
                      <span className="text-sm w-7 text-center font-medium">{item.quantity}</span>
                      <button onClick={() => updateQty(item.product_id, item.quantity + 1)} className="w-8 h-8 rounded-full hover:bg-peach/40 text-lapis" data-testid={`cart-page-increase-${item.slug}`}>
                        <Plus size={14} className="mx-auto" />
                      </button>
                    </div>
                    <button onClick={() => removeItem(item.product_id)} className="text-xs uppercase tracking-widest text-ink/40 hover:text-peach-deep" data-testid={`cart-page-remove-${item.slug}`}>Remove</button>
                  </div>
                </div>
                <div className="font-serif text-xl text-lapis whitespace-nowrap">{format(item.price * item.quantity, item.price_inr ? item.price_inr * item.quantity : null)}</div>
              </div>
            ))}
            <button onClick={clearCart} className="text-xs uppercase tracking-widest text-ink/40 hover:text-peach-deep" data-testid="cart-page-clear">Clear basket</button>

            {/* Shipping form — only when cart has physical items */}
            {hasPhysical && (
              <div className="glass-strong p-7 rounded-[1.75rem] mt-8" data-testid="cart-shipping-form">
                <div className="flex items-center gap-2 mb-5">
                  <Truck size={16} className="text-lapis" />
                  <div className="overline">Where shall I send it?</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                  <div>
                    <label className="overline block mb-2">Full name</label>
                    <input required className="eth-input" value={shipping.name} onChange={(e) => setShipping({ ...shipping, name: e.target.value })} data-testid="cart-shipping-name" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Email</label>
                    <input required type="email" className="eth-input" value={shipping.email} onChange={(e) => setShipping({ ...shipping, email: e.target.value })} data-testid="cart-shipping-email" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline block mb-2">Phone</label>
                    <input className="eth-input" value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} data-testid="cart-shipping-phone" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline block mb-2">Address line 1</label>
                    <input required className="eth-input" value={shipping.address_line1} onChange={(e) => setShipping({ ...shipping, address_line1: e.target.value })} data-testid="cart-shipping-address1" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline block mb-2">Address line 2 (optional)</label>
                    <input className="eth-input" value={shipping.address_line2} onChange={(e) => setShipping({ ...shipping, address_line2: e.target.value })} data-testid="cart-shipping-address2" />
                  </div>
                  <div>
                    <label className="overline block mb-2">City</label>
                    <input required className="eth-input" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} data-testid="cart-shipping-city" />
                  </div>
                  <div>
                    <label className="overline block mb-2">State / Province</label>
                    <input className="eth-input" value={shipping.state} onChange={(e) => setShipping({ ...shipping, state: e.target.value })} data-testid="cart-shipping-state" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Postal code</label>
                    <input required className="eth-input" value={shipping.postal_code} onChange={(e) => setShipping({ ...shipping, postal_code: e.target.value })} data-testid="cart-shipping-postal" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Country</label>
                    <input required className="eth-input" value={shipping.country} onChange={(e) => setShipping({ ...shipping, country: e.target.value })} data-testid="cart-shipping-country" />
                  </div>
                </div>
              </div>
            )}
            {!hasPhysical && (
              <div className="glass-strong p-7 rounded-[1.75rem] mt-8" data-testid="cart-buyer-form">
                <div className="flex items-center gap-2 mb-2">
                  <Mail size={16} className="text-lapis" />
                  <div className="overline">Delivery details</div>
                </div>
                <p className="text-xs text-ink/55 mb-5">Your download links arrive at this email the moment payment completes.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                  <div>
                    <label className="overline block mb-2">Your name</label>
                    <input className="eth-input" value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} data-testid="cart-buyer-name" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Email</label>
                    <input required type="email" className="eth-input" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} data-testid="cart-buyer-email" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="glass-strong p-7 rounded-[1.75rem] sticky top-28">
              <div className="overline mb-4">Summary</div>
              <div className="space-y-3 mb-6 text-sm text-ink/70">
                <div className="flex justify-between"><span>Items</span><span>{items.reduce((s, i) => s + i.quantity, 0)}</span></div>
                <div className="flex justify-between"><span>Subtotal</span><span>{format(subtotal)}</span></div>
                {hasPhysical && <div className="flex justify-between text-xs italic text-ink/55"><span>Shipping</span><span>Calculated at checkout</span></div>}
              </div>
              <div className="divider-soft mb-6" />
              <div className="flex items-baseline justify-between mb-6">
                <span className="overline">Total</span>
                <span className="font-serif text-3xl text-lapis" data-testid="cart-page-total">{format(subtotal)}</span>
              </div>
              <button onClick={checkout} disabled={loading} className="btn-primary w-full justify-center" data-testid="cart-page-checkout">
                {loading ? "Opening checkout…" : "Proceed to checkout"}
              </button>
              <p className="text-xs text-ink/50 text-center mt-4">Secure payment via Razorpay · UPI, cards & netbanking</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
